import type { TrackingFn } from '@change-corgi/core/react/tracking';

import type { ContextValue } from '../../components/SunriseHoldoutExperimentContextProvider';

import type { IsSunriseEnabledProps } from './isSunriseEnabledState';
import type { VariantState } from './variantState';

import type { Props } from './index';

export type MembershipFormProps = IsSunriseEnabledProps & {
	onSelectNo: () => void;
	onSelectYes: () => void;
	acceptMembership: boolean;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
};

export type MembershipFormState = IsSunriseEnabledProps & {
	noSelected: boolean;
	yesSelected: boolean;
	onSelectNo: () => void;
	onSelectYes: () => void;
	acceptMembership: boolean;
	setUserInteracted: ContextValue['setUserInteracted'];
	noSelectionMadeError: ContextValue['noSelectionMadeError'];
	onSubmit: MembershipFormProps['onSubmit'];
} & VariantState;

export function isMembershipFormState(props: Props): props is MembershipFormProps {
	return 'acceptMembership' in props;
}

export function membershipFormState(
	props: MembershipFormProps & {
		needsUserInteraction: boolean;
		isSunriseEnabled: boolean;
		enabled: boolean;
		setUserInteracted: ContextValue['setUserInteracted'];
		noSelectionMadeError: ContextValue['noSelectionMadeError'];
		setNoSelectionMadeError: ContextValue['setNoSelectionMadeError'];
		variant: ContextValue['variant'];
		track: TrackingFn;
	},
): MembershipFormState {
	const yesSelected = props.needsUserInteraction ? false : props.acceptMembership;
	const noSelected = props.needsUserInteraction ? false : !props.acceptMembership;

	function handleSelection(callback: MembershipFormProps['onSelectNo'] | MembershipFormProps['onSelectYes']) {
		return () => {
			props.setUserInteracted(true);
			props.setNoSelectionMadeError(false);
			callback();
		};
	}

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		if (!yesSelected && !noSelected && props.variant === 'variant_2') {
			e.preventDefault();
			props.setNoSelectionMadeError(true);
			void props.track('post_promote_member_offer_no_selection');

			return;
		}

		await props.onSubmit(e);
	};

	return {
		isSunriseEnabled: props.isSunriseEnabled,
		yesSelected,
		noSelected,
		onSelectNo: handleSelection(props.onSelectNo),
		onSelectYes: handleSelection(props.onSelectYes),
		acceptMembership: yesSelected,
		setUserInteracted: props.setUserInteracted,
		noSelectionMadeError: props.enabled && props.noSelectionMadeError,
		onSubmit: handleSubmit,
		variant: props.variant,
	};
}
